import { useEffect } from 'react';
import { useAuthStore } from '../stores';
import { getAuthClient } from '../utils/auth';
import { isPublicPlatform } from '../utils/publicPlatforms';

const auth = getAuthClient();

/**
 * Custom hook to check if the logged-in user has access to the current community.
 *
 * This check only runs when:
 * - User is logged in (not in kiosk/public mode)
 * - We have a community NID from the config
 * - The access hasn't been checked yet
 * - Platform is 'yt' (not page, siteSee, kiosk, etc.)
 *
 * If the user doesn't have access, it sets the access denied state in the auth store,
 * which is consumed by AppSwitchboard to show an error screen.
 *
 * @param {Object} options - Configuration options
 * @param {boolean} options.isLoggedIn - Whether the user is logged in
 * @param {boolean} options.isPublic - Whether this is a public/kiosk instance
 * @param {string} options.platform - The current platform (yt, page, siteSee, kiosk, etc.)
 * @param {number|null} options.communityNid - The community NID to check access for
 */
const useCommunityAccess = ({
  isLoggedIn,
  isPublic,
  platform,
  communityNid,
}) => {
  const accessChecked = useAuthStore((state) => state.accessChecked);
  const hasAccess = useAuthStore((state) => state.hasAccess);
  const accessDeniedMessage = useAuthStore((state) => state.accessDeniedMessage);
  const denyAccess = useAuthStore((state) => state.denyAccess);
  const grantAccess = useAuthStore((state) => state.grantAccess);

  useEffect(() => {
    // Skip check for public pages (PIB, SiteSee, print pages, etc.)
    const shouldSkipForPlatform = isPublicPlatform(platform);

    // Skip check if:
    // - Already checked
    // - Not logged in (kiosk/public modes don't need this check)
    // - No community NID yet
    // - Is public mode (kiosk, siteSee, etc.)
    // - Platform should skip access check (page, siteSee, kiosk, etc.)
    if (accessChecked || !isLoggedIn || !communityNid || isPublic || shouldSkipForPlatform) {
      // Grant access automatically for skipped cases
      if (!accessChecked && (isPublic || shouldSkipForPlatform || !isLoggedIn)) {
        grantAccess();
      }
      return;
    }

    const checkAccess = async () => {
      try {
        const url = `/your_tour_api/community_access/${communityNid}?_format=json`;
        const response = await auth.fetchWithAuthentication(url, 'GET', null);

        if (response?.hasAccess) {
          grantAccess();
        } else {
          // Use the API message which includes the user's name and community name
          const message = response?.message || 'You do not have access to this community.';
          denyAccess(message);
        }
      } catch (error) {
        console.error('Failed to check community access:', error);
        // On error, deny access to be safe
        denyAccess('Unable to verify your access to this community. Please try again.');
      }
    };

    checkAccess();
  }, [
    isLoggedIn,
    isPublic,
    platform,
    communityNid,
    accessChecked,
    denyAccess,
    grantAccess,
  ]);

  return {
    accessChecked,
    hasAccess,
    accessDeniedMessage,
  };
};

export default useCommunityAccess;
